import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useCopy } from "@/lib/copyVariant";
import { img, imgSrcSet } from "@/lib/img";

// Lifestyle photos - real moments. Stored as a flat array; layout below
// places them into an asymmetric collage on desktop and a horizontal
// snap-carousel on mobile.
const SHOTS = [
  {
    src: "https://customer-assets.emergentagent.com/job_0fe81fb6-fc15-4971-9e6a-3442db856be6/artifacts/6mz3u6t8_PHOTO-2026-05-26-16-54-40.jpg",
    width: 921,
    height: 1280,
    tint: "#c9b291",
    alt: "Two people playing Calmasutras across a sunlit table with tea, biscuits and a drink",
    caption: "An afternoon, two hands, fifty-two ideas in the room.",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_0fe81fb6-fc15-4971-9e6a-3442db856be6/artifacts/t9xkhpjh_PHOTO-2026-05-26-16-54-31.jpg",
    width: 960,
    height: 1280,
    tint: "#b9a684",
    alt: "Overhead view of Calmasutras cards spread across a stone table with tea and a gingerbread cookie",
    caption: "Sometimes you play it. Sometimes it plays you.",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_0fe81fb6-fc15-4971-9e6a-3442db856be6/artifacts/ul9ac0o6_PHOTO-2026-05-26-17-02-54.jpg",
    width: 1200,
    height: 1600,
    tint: "#a89e8a",
    alt: "Calmasutras deck on a restaurant table beside a wine bottle, sunflower and a small gingerbread figure",
    caption: "Looks at home next to the sunflowers.",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_0fe81fb6-fc15-4971-9e6a-3442db856be6/artifacts/13mnpfyw_PHOTO-2026-05-26-17-02-54%202.jpg",
    width: 1200,
    height: 1600,
    tint: "#bcae93",
    alt: "Overhead lifestyle scene of Calmasutras cards with teapot, cup, sunflower and cocktails",
    caption: "Tea, talk, and the kind of cards you actually want to deal.",
  },
];

export default function LifestyleSection() {
  const { c } = useCopy();

  // Kick off the largest image right away so it's already cached by the time
  // the user scrolls down to the section. Cheap, no layout cost.
  useEffect(() => {
    const heads = SHOTS.slice(0, 2);
    const links = heads.map((s) => {
      const l = document.createElement("link");
      l.rel = "preload";
      l.as = "image";
      l.href = img(s.src, { w: 900 });
      l.fetchPriority = "high";
      document.head.appendChild(l);
      return l;
    });
    return () => links.forEach((l) => l.parentNode && l.parentNode.removeChild(l));
  }, []);

  return (
    <section
      id="in-the-wild"
      className="relative bg-[#fff8f3] py-16 sm:py-24 md:py-32 px-5 md:px-10 overflow-hidden"
      data-testid="lifestyle-section"
    >
      <div className="max-w-[1400px] mx-auto">
        {/* Header */}
        <div className="max-w-3xl">
          <motion.span
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-[10px] sm:text-[11px] uppercase tracking-[0.5em] text-[#96aeb4] font-semibold block mb-5"
          >
            {c("lifestyleEyebrow")}
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-[56px] font-bold text-[#464646] leading-[1.05] tracking-tight"
          >
            {c("lifestyleHeadingL1")}
            <br />
            <span className="italic text-[#7a9d83]">{c("lifestyleHeadingL2")}</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2, duration: 0.7 }}
            className="mt-4 sm:mt-5 text-sm sm:text-base text-[#464646]/65 max-w-xl leading-relaxed"
          >
            {c("lifestyleBody")}
          </motion.p>
        </div>

        {/* Desktop / tablet: asymmetric two-column collage */}
        <div className="hidden md:grid mt-14 lg:mt-20 grid-cols-12 gap-6 lg:gap-8">
          {/* Column A */}
          <div className="col-span-7 flex flex-col gap-6 lg:gap-8">
            <Shot shot={SHOTS[0]} i={0} priority className="aspect-[5/6] lg:aspect-[5/5.5]" />
            <Shot shot={SHOTS[2]} i={2} className="aspect-[5/4] lg:aspect-[6/4]" />
          </div>
          {/* Column B - offset down slightly to break the grid */}
          <div className="col-span-5 flex flex-col gap-6 lg:gap-8 lg:pt-20">
            <Shot shot={SHOTS[1]} i={1} priority className="aspect-square" />
            <Shot shot={SHOTS[3]} i={3} className="aspect-[4/5]" />
          </div>
        </div>

        {/* Mobile: snap carousel */}
        <div
          className="md:hidden mt-10 -mx-5 px-5 flex gap-4 overflow-x-auto snap-x snap-mandatory pb-4"
          data-testid="lifestyle-mobile-carousel"
        >
          {SHOTS.map((s, i) => (
            <div key={i} className="shrink-0 w-[78vw] snap-start">
              <Shot shot={s} i={i} priority={i === 0} className="aspect-[5/6]" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Shot({ shot, i, className = "", priority = false }) {
  const [loaded, setLoaded] = useState(false);

  return (
    <motion.figure
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.7, delay: i * 0.08, ease: "easeOut" }}
      className="group relative"
      data-testid={`lifestyle-shot-${i + 1}`}
    >
      <div
        className={`relative overflow-hidden rounded-2xl sm:rounded-3xl shadow-lg shadow-[#464646]/10 ${className}`}
        style={{ backgroundColor: shot.tint }}
      >
        <img
          src={img(shot.src, { w: 900 })}
          srcSet={imgSrcSet(shot.src, [400, 600, 900, 1200])}
          sizes="(min-width: 1024px) 720px, (min-width: 768px) 50vw, 78vw"
          alt={shot.alt}
          width={shot.width}
          height={shot.height}
          loading={priority ? "eager" : "lazy"}
          decoding="async"
          fetchPriority={priority ? "high" : "auto"}
          onLoad={() => setLoaded(true)}
          className={`absolute inset-0 w-full h-full object-cover transition-[opacity,transform] duration-[1.6s] ease-out group-hover:scale-[1.04] ${
            loaded ? "opacity-100" : "opacity-0"
          }`}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/35 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      </div>
      <figcaption className="mt-3 text-xs sm:text-sm text-[#464646]/70 italic leading-snug">
        {shot.caption}
      </figcaption>
    </motion.figure>
  );
}
